import {
	BlockControls,
	useBlockProps,
	useInnerBlocksProps,
} from '@wordpress/block-editor';
import { ToolbarButton, ToolbarGroup, ToolbarItem } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { useCallback, useEffect, useRef, useState } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';

import useStepPlayback from '../../_shared/slider-preview-playback';
import { getBlockGapValue } from '../../../utils/helpers';
import RotatorInspectorControls from './InspectorControls';

// Importing the stylesheet here is what creates the block's style-index.css
// entry; wp-scripts does not pick style.scss up from block.json alone.
import '../style.scss';

const ALLOWED_BLOCKS = [ 'brandy/rotator-slot' ];

const TEMPLATE = [
	[ 'brandy/rotator-slot' ],
	[ 'brandy/rotator-slot' ],
	[ 'brandy/rotator-slot' ],
	[ 'brandy/rotator-slot' ],
	[ 'brandy/rotator-slot' ],
];

/**
 * The canvas shows one item per slot — the same composed row a visitor sees —
 * and the toolbar stepper chooses which one.
 *
 * Laying every child out in a column instead was the obvious first answer, and
 * it is wrong twice over: the row stops looking like the thing being designed,
 * and a five-slot rotator becomes a wall of fifteen images. Showing one set at
 * a time keeps the canvas honest, and the stepper is what keeps the other sets
 * reachable.
 *
 * Selection overrides the stepper per slot. Picking a hidden image out of the
 * List View has to reveal it, or the block editor's own navigation would lead
 * authors somewhere they cannot see.
 *
 * Playback is off unless asked for. A slider's other slides are off-screen but
 * still scrollable to; a rotator's are hidden outright, so a block that played
 * by default would be a block nobody could edit.
 */
export default function Edit( { attributes, setAttributes, clientId } ) {
	const { interval, stagger, preset, alignment, duration, columns } =
		attributes;

	const [ playOverride, setPlayOverride ] = useState( null );
	const [ editIndex, setEditIndex ] = useState( 0 );
	const rowRef = useRef( null );

	const { slotChildIds, isPreviewMode, selectionChain } = useSelect(
		( select ) => {
			const store = select( 'core/block-editor' );
			const selectedId = store.getSelectedBlockClientId();

			return {
				// One entry per slot, holding that slot's child clientIds in
				// order — enough to map a selection back to a child index.
				slotChildIds: store
					.getBlocks( clientId )
					.map( ( slot ) =>
						slot.innerBlocks.map( ( child ) => child.clientId )
					),
				/*
				 * True inside a BlockPreview — the inserter's hover preview and
				 * the pattern list. Nobody is editing there, and a row parked
				 * on its first set is indistinguishable from a plain grid of
				 * images, so the rotation is most of what needs showing.
				 */
				isPreviewMode: store.getSettings().isPreviewMode,
				// The selected block plus every ancestor, so a selection deep
				// inside a child still resolves to the child that holds it.
				selectionChain: selectedId
					? [ selectedId, ...store.getBlockParents( selectedId, true ) ]
					: [],
			};
		},
		[ clientId ]
	);

	// The longest slot sets the cycle; shorter ones wrap early against their
	// own length, exactly as they do on the frontend.
	const steps = Math.max( 1, ...slotChildIds.map( ( ids ) => ids.length ) );

	const isPlaying = ( playOverride ?? isPreviewMode ) && steps > 1;

	const { index: playIndex } = useStepPlayback( {
		count: steps,
		delay: interval,
		playing: isPlaying,
		loop: true,
	} );

	/*
	 * Applying the index is a class toggle, the same one view.js performs. Done
	 * imperatively because the children are inner blocks — the parent has their
	 * DOM but not their React tree.
	 */
	useEffect( () => {
		const row = rowRef.current;

		if ( ! row ) {
			return;
		}

		const shown = isPlaying ? playIndex : editIndex;

		Array.from( row.querySelectorAll( '.brandy-rotator-slot' ) ).forEach(
			( slot, slotIndex ) => {
				slot.style.setProperty(
					'--brandy-rotator-delay',
					`${ isPlaying ? stagger * slotIndex : 0 }ms`
				);

				const children = Array.from( slot.children );

				if ( ! children.length ) {
					return;
				}

				// A selection inside this slot wins over the stepper, so the
				// List View can always bring a hidden child into sight.
				const ids = slotChildIds[ slotIndex ] || [];
				const selected = ids.findIndex( ( id ) =>
					selectionChain.includes( id )
				);

				const target =
					selected >= 0 ? selected : shown % children.length;

				children.forEach( ( child, childIndex ) => {
					child.classList.remove( 'is-leaving' );
					child.classList.toggle( 'is-active', childIndex === target );
				} );
			}
		);
	}, [
		playIndex,
		editIndex,
		isPlaying,
		stagger,
		slotChildIds,
		selectionChain,
	] );

	// Stepping is browsing, not playing, so it releases any running preview.
	const step = useCallback(
		( delta ) => {
			setPlayOverride( false );
			setEditIndex( ( current ) => ( current + delta + steps ) % steps );
		},
		[ steps ]
	);

	const togglePlay = useCallback(
		() => setPlayOverride( ( current ) => ! ( current ?? false ) ),
		[]
	);

	const shownIndex = isPlaying ? playIndex % steps : editIndex % steps;

	const gap = getBlockGapValue( attributes.style?.spacing?.blockGap );

	// Centre is the default and emits no class, matching render.php so the
	// canvas and the front end resolve to the same selectors.
	const alignClass =
		alignment === 'left' || alignment === 'right'
			? ` is-align-${ alignment }`
			: '';

	const blockProps = useBlockProps( {
		className: `brandy-rotator ${
			isPlaying ? 'is-live' : 'is-editing'
		}${ alignClass }`,
		'data-preset': preset,
		style: {
			'--brandy-rotator-duration': `${ duration }ms`,
			'--brandy-rotator-stagger': `${ stagger }ms`,
			'--brandy-rotator-columns': columns?.desktop ?? 5,
			'--brandy-rotator-columns-tablet': columns?.tablet ?? 3,
			'--brandy-rotator-columns-mobile': columns?.mobile ?? 2,
			// Core skips serializing blockGap for this block (block.json), so
			// the canvas only reflects the control if we apply it ourselves.
			...( gap ? { '--brandy-rotator-gap': gap } : {} ),
		},
	} );

	const innerBlocksProps = useInnerBlocksProps(
		{ className: 'brandy-rotator__row', ref: rowRef },
		{
			allowedBlocks: ALLOWED_BLOCKS,
			template: TEMPLATE,
			orientation: 'horizontal',
		}
	);

	return (
		<>
			<BlockControls>
				<ToolbarGroup>
					<ToolbarButton
						icon="arrow-left-alt2"
						label={ __( 'Show previous item', 'brandy-blocks' ) }
						onClick={ () => step( -1 ) }
						disabled={ steps < 2 }
					/>
					<ToolbarItem
						as="span"
						className="brandy-rotator__step-count"
					>
						{ sprintf(
							/* translators: 1: current item number, 2: total items. */
							__( '%1$d / %2$d', 'brandy-blocks' ),
							shownIndex + 1,
							steps
						) }
					</ToolbarItem>
					<ToolbarButton
						icon="arrow-right-alt2"
						label={ __( 'Show next item', 'brandy-blocks' ) }
						onClick={ () => step( 1 ) }
						disabled={ steps < 2 }
					/>
					<ToolbarButton
						icon="controls-play"
						label={ __( 'Play rotation', 'brandy-blocks' ) }
						onClick={ togglePlay }
						isPressed={ isPlaying }
						disabled={ steps < 2 }
					/>
				</ToolbarGroup>
			</BlockControls>

			<RotatorInspectorControls
				attributes={ attributes }
				setAttributes={ setAttributes }
			/>

			<div { ...blockProps }>
				<div { ...innerBlocksProps } />
			</div>
		</>
	);
}
